import {
  AccountBalanceWalletOutlined,
  CampaignOutlined,
  DescriptionOutlined,
  GroupsOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

function getPractitionerName(practitioner) {
  return (
    practitioner?.name ||
    practitioner?.fullName ||
    practitioner?.displayName ||
    [practitioner?.firstName, practitioner?.lastName].filter(Boolean).join(' ') ||
    'Therapist'
  )
}

export default function TherapistPayoutHubPage() {
  const { mentorSlug } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [therapist, setTherapist] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let active = true
    async function loadTherapist() {
      setLoading(true)
      setLoadError('')
      try {
        const response = await getMinimalMentorData(
          mentorSlug,
          true,
          `/api/secure/cc/support/therapists/by-slug?mentor-slug=${mentorSlug}`,
        )
        if (active) {
          setTherapist(response || null)
        }
      } catch (error) {
        console.error('Error loading therapist payout hub:', error)
        if (active) {
          setLoadError('Unable to load therapist details.')
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadTherapist()
    return () => {
      active = false
    }
  }, [mentorSlug])

  const basePath = `/support-home/practitioner-management/therapists/${mentorSlug}`
  const cards = useMemo(
    () => [
      {
        id: 'engagement-models',
        title: 'Engagement Models',
        description: 'Manage fee and engagement models that drive payout calculations.',
        path: `${basePath}/engagement-models`,
        icon: <GroupsOutlined />,
      },
      {
        id: 'payout-reports',
        title: 'Payout Reports',
        description: 'Generate, export, and review include-all-sessions payout reports.',
        path: `${basePath}/payout-reports`,
        icon: <DescriptionOutlined />,
      },
      {
        id: 'payout-receipts',
        title: 'Payout Receipts',
        description: 'View payout receipt records and practitioner-facing receipt status.',
        path: `${basePath}/payout-receipts`,
        icon: <ReceiptLongOutlined />,
      },
      {
        id: 'communications',
        title: 'Communications',
        description: 'Send payout reports, therapy report reminders, and generic messages.',
        path: `${basePath}/communications`,
        icon: <CampaignOutlined />,
      },
    ],
    [basePath],
  )

  const therapistName = getPractitionerName(therapist)

  return (
    <Box sx={{ minHeight: '100%', bgcolor: '#f8fafc' }}>
      <SupportHeader
        heading={`${therapistName} Payouts`}
        subHeading="Manage payout configuration surfaces and payout-related communication from one place."
      />
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">
              Loading payout workspace...
            </Typography>
          </Stack>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <AccountBalanceWalletOutlined color="primary" />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {therapistName}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={therapist?.id || 'Therapist'} />
                    {therapist?.slug && <Chip size="small" variant="outlined" label={therapist.slug} />}
                  </Stack>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                onClick={() => navigate('/support-home/practitioner-management/therapists')}
              >
                Back to Therapists
              </Button>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {cards.map((card) => (
                <Paper
                  key={card.id}
                  variant="outlined"
                  sx={{
                    p: 2.25,
                    borderRadius: 2,
                    minHeight: 174,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: 'primary.50',
                          color: 'primary.main',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 800 }}>
                        {card.title}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </Stack>
                  <Box>
                    <Button variant="contained" onClick={() => navigate(card.path)}>
                      Open
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  )
}
